/* eslint-disable max-len */
/* eslint-disable react/jsx-one-expression-per-line */
import React, { useContext, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import * as qs from 'query-string';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AccountContext } from '../../../components/AccountContext';
import { NotificationContext } from '../../../components/GrowlNotifications';
import { Loading } from '../../../components/Loading';
import { growlErrors } from '../../../utils/NotificationUtil';
import { compare } from '../../../utils/AppUtil';
import SendPaymentLinkForm from '../components/SendPaymentLinkForm';
import BuyCreditsForm from '../components/BuyCreditsForm';
import { BUY_CREDITS_FORM_QUERY, SEND_PAYMENT_LINK_MUTATION } from '../services/buy-credits.service';
import { computeCostInUsd, getAmountOfCreditsAfterSuccessfulTransfer } from '../helpers/buy-credits.helper';
import CreditPaymentFinalized from '../components/CreditPaymentFinalized';
import { AccountType, PayedBy } from '../../../shared/Constants';
import ContactGpiRepresentativeToBuyCredits from '../components/ContactGpiRepresentativeToBuyCredits';

const DEFAULT_VIEWER_DATA = {
  firstName: '',
  lastName: '',
};

const DEFAULT_ACCOUNT_DATA = {
  type: null,
  displayName: '',
  payedBy: null,
  gpiRepresentativeData: {
    parentAccountOwnerFullName: '',
    parentAccountOwnerEmail: '',
  },
};

const BuyCredits = () => {
  const [user, setUser] = useState(DEFAULT_VIEWER_DATA);
  const [accountData, setAccountData] = useState(DEFAULT_ACCOUNT_DATA);
  const [sendPaymentLinkDialogVisible, setSendPaymentLinkDialogVisible] = useState(false);
  const [selectedCreditOption, setSelectedCreditOption] = useState(0);
  const [availableCreditValues, setAvailableCreditValues] = useState([]);
  const [supportedLanguages, setSupportedLanguages] = useState([]);

  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);

  const {
    cldr: { localeDisplayName },
  } = useSelector((state) => state.i18n);

  const location = useLocation();
  const { result, amount } = qs.parse(location.search);

  const [sendPaymentLinkMutation] = useMutation(SEND_PAYMENT_LINK_MUTATION);

  const { loading, error, data } = useQuery(BUY_CREDITS_FORM_QUERY, {
    variables: { accountId: currentAccount },
    fetchPolicy: 'no-cache',
  });

  const { viewer, account, creditValues, activeLocales } = data;

  useEffect(() => {
    const dataIsAvailable =
      viewer && account && creditValues && creditValues.length > 0 && activeLocales && activeLocales.length > 0;

    if (dataIsAvailable) {
      const availableSupportedLanguages = activeLocales.map((lang) => ({
        value: lang,
        label: localeDisplayName(lang),
      }));

      const sortedSupportedLanguages = availableSupportedLanguages.sort(compare);

      setSelectedCreditOption(creditValues[0].credit);
      setAvailableCreditValues(creditValues);
      setSupportedLanguages(sortedSupportedLanguages);
      setUser(viewer);
      setAccountData({
        type: account.type,
        displayName: account.companyInformation.displayName,
        payedBy:
          !!account.subscriptionInformation && !!account.subscriptionInformation.payedBy
            ? account.subscriptionInformation.payedBy
            : null,
        gpiRepresentativeData: account.gpiRepresentativeData,
      });
    }
  }, [account, creditValues, viewer, activeLocales, localeDisplayName]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    growlErrors(growl, error);
  }

  if (result) {
    const amountOfCredits = getAmountOfCreditsAfterSuccessfulTransfer(amount);

    return (
      <CreditPaymentFinalized
        paymentResultType={result}
        amountOfCredits={amountOfCredits}
        accountName={account.displayName}
      />
    );
  }

  const computedCostInUsd = computeCostInUsd(selectedCreditOption, availableCreditValues);

  if (accountData.type === AccountType.ESSENTIAL && accountData.payedBy === PayedBy.PAYED_BY_PARENT) {
    return <ContactGpiRepresentativeToBuyCredits gpiRepresentativeData={account.gpiRepresentativeData} />;
  }

  return (
    <>
      <SendPaymentLinkForm
        sendPaymentLinkDialogVisible={sendPaymentLinkDialogVisible}
        hideForm={() => setSendPaymentLinkDialogVisible(false)}
        additionalInput={{
          amountOfCredits: selectedCreditOption,
          accountId: currentAccount,
        }}
        supportedLanguages={supportedLanguages}
        sendCreditLinkMutation={sendPaymentLinkMutation}
      />
      <BuyCreditsForm
        viewer={user}
        accountDisplayName={accountData.displayName}
        computedCostInUsd={computedCostInUsd}
        selectedCreditOption={selectedCreditOption}
        creditValues={creditValues}
        setSelectedCreditOption={(value) => setSelectedCreditOption(value)}
        showPaymentDialog={() => setSendPaymentLinkDialogVisible(true)}
      />
    </>
  );
};

export default BuyCredits;
